import { t } from '@lingui/macro';
import { useEffect, useRef, useState } from 'react';
import { useTestTypeContext } from './UseTestTypeContext';
import useUiAlerts, { createAlertConfig, WARNING } from '../../../../../stores/AlertsStore';
import { useDataService } from '../../../../../services/ApiDataService';
import { AGENT_PICKER_URL, buildAgentFilterParams } from './AgentSelectorHelpers';
import { DEFAULT, WIRELESS } from '../../ServiceTestsConstants';
import { isBttTestType } from '../TestHelper';
import { is2KAgent } from '../../../../panels/net-test-now-panels/NetTestNowHelpers';

const normalizeAgents = (result) => (Array.isArray(result) ? result : result?.data || []);

export default function useAgents() {
    const [wirelessAgents, setWirelessAgents] = useState([]);
    const [defaultAgents, setDefaultAgents] = useState([]);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [matchingTaggedAgents, setMatchingTaggedAgents] = useState([]);

    const { testType } = useTestTypeContext();
    const isBtt = isBttTestType(testType?.bttTest, testType?.showBttAuth);

    const hasFetchedRef = useRef(false);
    const [, uiAlertActions] = useUiAlerts();
    const dataService = useDataService();

    useEffect(() => {
        if (hasFetchedRef.current) {
            return;
        }

        let cancelled = false;

        const fetchAgents = async (agentType, errorMessage) => {
            const params = buildAgentFilterParams(testType, agentType);

            return dataService
                .get(AGENT_PICKER_URL, params)
                .then((result) => {
                    let agents = normalizeAgents(result);

                    if (isBtt) {
                        agents = agents.filter((agent) => !is2KAgent(agent));
                    }

                    return agents;
                })
                .catch(() => {
                    uiAlertActions.createAlert(createAlertConfig(WARNING, errorMessage));
                    return [];
                });
        };

        const loadWirelessAndDefaultAgents = async () => {
            try {
                const [tmpWirelessAgents, tmpDefaultAgents] = await Promise.all([
                    fetchAgents(WIRELESS, t`Error to get wireless nPoints.`),
                    fetchAgents(DEFAULT, t`Error to get default nPoints.`),
                ]);

                if (cancelled) {
                    return;
                }

                setWirelessAgents(tmpWirelessAgents);
                setDefaultAgents(tmpDefaultAgents);
                hasFetchedRef.current = true;
            } catch {
                uiAlertActions.createAlert(
                    createAlertConfig(WARNING, t`Error to get load Wireless And Default Agents.`)
                );
            }
        };

        loadWirelessAndDefaultAgents();

        return () => {
            cancelled = true;
        };
    }, [dataService, isBtt, testType, uiAlertActions]);

    return {
        wirelessAgents,
        defaultAgents,
        availableAgents,
        matchingTaggedAgents,
        setAvailableAgents,
        setMatchingTaggedAgents,
    };
}
